<template>
  <div>
    <v-radio-group
      v-if="showMethodSelector && applePayAvailable"
      v-model="paymentSource" row :disabled="disabled || !cardEl"
      @change="selectPaymentSource"
    >
      <v-radio label="Credit card" value="card"></v-radio>
      <v-radio label="Apple Pay" value="applePay"></v-radio>
    </v-radio-group>
    <p v-if="paymentSource === 'applePay'" class="text-body-2">
      Continue to confirm your payment with Apple Pay.
    </p>
    <div
      v-show="paymentSource === 'card'"
      ref="cardContainer"
      class="credit-card"
      :id="mountTargetId"
    ></div>
    <div class="v-text-field__details">
      <div class="v-messages theme--light error--text" role="alert">
        <div class="v-messages__wrapper">
          <div class="v-messages__message">{{ error }}</div>
        </div>
      </div>
    </div>
    <v-row v-if="paymentSource === 'card'">
      <v-col cols="12">
        Note:
        <ul>
          <li>When making credit card payment, do complete the card's 3D Secure authorisation if
            prompted.</li>
        </ul>
      </v-col>
    </v-row>
    <airwallex-apple-pay ref="applePay" :currency="currency" :label="merchantLabel" />
  </div>
</template>

<script>
import AirwallexApplePay from './AirwallexApplePay.vue';
import { supportsAirwallexApplePay } from '../../../plugins/airwallex-apple-pay';
import { toCreditCardPaymentMessage } from '../../../plugins/airwallex-payment';
import {
  claimAirwallexCardElement,
  loadAirwallexSdk,
  ownsAirwallexCardElement,
  releaseAirwallexCardElement,
} from '../../../plugins/airwallex-sdk';

let airwallexCardElementSequence = 0;

function nextAirwallexCardElementId() {
  airwallexCardElementSequence += 1;
  return `airwallex-card-element-${airwallexCardElementSequence}`;
}

export default {
  name: 'AirwallexPayment',
  components: { AirwallexApplePay },
  data() {
    return {
      cardEl: null,
      invalid: true,
      error: '',
      mountTargetId: nextAirwallexCardElementId(),
      airwallexSdk: null,
      airwallexDestroyed: false,
      paymentSource: this.applePay ? 'applePay' : 'card',
      currency: String(window.systemCurrency || '').toUpperCase(),
    };
  },
  computed: {
    applePayAvailable() {
      return (this.allowApplePay || this.applePay) && supportsAirwallexApplePay(this.currency);
    },
    merchantLabel() {
      const brand = this.$store && this.$store.state.brand;
      if (brand === 'cldy') return 'CLDY.com PTE. LTD.';
      if (brand === 'qoxy') return 'Qoxy';
      return 'GOCLOUDEASY PTE. LTD.';
    },
  },
  mounted() {
    this.$emit('update:invalid', true);
    this.$emit('apple-pay-selected', this.applePay);
    this.initElement();
  },
  beforeDestroy() {
    this.airwallexDestroyed = true;
    this.invalid = true;
    this.$emit('interface', null);
    this.$emit('update:invalid', true);

    if (this.$refs.cardContainer) {
      this.$refs.cardContainer.removeEventListener(
        'onChange',
        this.handleCardChange,
      );
    }

    if (this.cardEl) {
      releaseAirwallexCardElement(
        this,
        this.cardEl,
        this.airwallexSdk,
      );
    }

    this.cardEl = null;
    this.airwallexSdk = null;
  },
  methods: {
    selectPaymentSource(source) {
      this.$emit('apple-pay-selected', source === 'applePay');
      // The parent may refuse the switch or wait for cancellation. Restore
      // the actual method, including the radio group's internal selection.
      this.$nextTick(() => {
        this.paymentSource = this.applePay ? 'applePay' : 'card';
      });
    },
    async initElement() {
      try {
        const airwallex = await loadAirwallexSdk();

        if (this.airwallexDestroyed) {
          return;
        }

        if (this.applePay && !supportsAirwallexApplePay(this.currency)) {
          throw new Error('Apple Pay is not available on this device.');
        }

        airwallex.init({
          env: process.env.VUE_APP_AIRWALLEX_ENV,
          origin: window.location.origin,
        });

        if (this.airwallexDestroyed) {
          return;
        }

        const cardElement = airwallex.createElement('card', {
          disabled: this.disabled,
        });

        this.airwallexSdk = airwallex;
        this.cardEl = cardElement;
        claimAirwallexCardElement(this, cardElement);
        cardElement.mount(this.mountTargetId);

        if (this.airwallexDestroyed) {
          releaseAirwallexCardElement(this, cardElement, airwallex);
          this.cardEl = null;
          return;
        }

        const container = this.$refs.cardContainer;

        if (!container) {
          throw new Error('The credit card payment form is unavailable.');
        }

        container.addEventListener('onChange', this.handleCardChange);
        if (this.applePay) {
          this.invalid = false;
          this.$emit('update:invalid', false);
        }
        this.$emit('interface', {
          confirmIntent: (intentId, clientSecret) => {
            if (!ownsAirwallexCardElement(this, cardElement)
              || this.airwallexDestroyed
            ) {
              return Promise.reject(new Error(
                'The credit card payment form is no longer available.',
              ));
            }

            if (this.paymentSource === 'applePay') {
              return this.$refs.applePay.confirmIntent(intentId, clientSecret, this.amount);
            }

            return airwallex.confirmPaymentIntent({
              element: cardElement,
              intent_id: intentId,
              client_secret: clientSecret,
              payment_method_options: {
                card: {
                  auto_capture: false,
                  three_ds_action: 'FORCE_3DS',
                },
              },
            });
          },
          createConsent: (customerId, clientSecret, currency) => {
            if (this.paymentSource === 'applePay') {
              return Promise.reject(new Error('Use the credit card form to save a card.'));
            }
            if (!ownsAirwallexCardElement(this, cardElement)
              || this.airwallexDestroyed
            ) {
              return Promise.reject(new Error(
                'The credit card payment form is no longer available.',
              ));
            }

            return airwallex.createPaymentConsent({
              customer_id: customerId,
              client_secret: clientSecret,
              currency,
              element: cardElement,
              next_triggered_by: 'merchant',
              merchant_trigger_reason: 'unscheduled',
              three_ds_action: 'FORCE_3DS',
            });
          },
        });
      } catch (error) {
        if (this.$refs.cardContainer) {
          this.$refs.cardContainer.removeEventListener(
            'onChange',
            this.handleCardChange,
          );
        }

        if (this.cardEl) {
          releaseAirwallexCardElement(
            this,
            this.cardEl,
            this.airwallexSdk,
          );
          this.cardEl = null;
        }
        this.airwallexSdk = null;

        if (!this.airwallexDestroyed) {
          this.invalid = true;
          this.error = toCreditCardPaymentMessage(
            (error && error.message)
              || 'The payment form could not be initialized.',
          );
          this.$emit('interface', null);
          this.$emit('update:invalid', true);
        }
      }
    },
    handleCardChange(event) {
      if (this.airwallexDestroyed
        || !this.cardEl
        || !ownsAirwallexCardElement(this, this.cardEl)
      ) {
        return;
      }

      const detail = event.detail || {};

      if (detail.complete) {
        this.invalid = false;
        this.error = '';
      } else {
        this.invalid = true;
        if (detail.error && detail.error.message) {
          this.error = toCreditCardPaymentMessage(detail.error.message);
        }
      }

      this.$emit('update:invalid', this.paymentSource === 'applePay' ? false : this.invalid);
    },
  },
  watch: {
    applePay(selected) {
      this.paymentSource = selected ? 'applePay' : 'card';
    },
    paymentSource(source) {
      this.error = '';
      this.$emit('update:invalid', source === 'applePay' ? !this.cardEl : this.invalid);
    },
    disabled(disabled) {
      if (this.cardEl
        && ownsAirwallexCardElement(this, this.cardEl)
        && typeof this.cardEl.update === 'function'
      ) {
        this.cardEl.update({ disabled });
      }
    },
  },
  props: {
    applePay: { type: Boolean, default: false },
    showMethodSelector: { type: Boolean, default: false },
    allowApplePay: {
      type: Boolean,
      default: false,
    },
    amount: {
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
};
</script>

<style scoped>
  .credit-card {
    padding: 10px;
    margin-bottom: 8px;
    border: 1px rgba(0,0,0,0.87) solid;
    border-radius: 4px;
  }
</style>
